import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Divider,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Bookmark,
  BookmarkBorder,
  ThumbUp,
  ThumbUpOutlined,
  Share,
  Comment,
  AccessTime,
  Person,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import axios from "axios";
import Comments from "../components/Comments";
import { publicUploadUrl } from "../utils/mediaUrls";

const apiUrl = process.env.REACT_APP_API_URL || "/api";

const Post = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { id } = useParams(); // Get post ID from URL params

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  // Fetch post data from API
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/posts/${id}`);
        setPost(response.data);
        setLikeCount(response.data.likes || 0);
        setError(null);
      } catch (err) {
        console.error("Error fetching post:", err);
        setError("Failed to load post. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPost();
    }
  }, [id]);

  // Format date
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Handle like button click
  const handleLikeClick = () => {
    if (liked) {
      setLikeCount(likeCount - 1);
    } else {
      setLikeCount(likeCount + 1);
    }
    setLiked(!liked);
  };

  // Handle bookmark button click
  const handleBookmarkClick = () => {
    setBookmarked(!bookmarked);
  };

  // Handle share button click
  const handleShareClick = () => {
    if (navigator.share && post) {
      navigator.share({
        title: post.title,
        text: post.description,
        url: window.location.href,
      });
    } else {
      // Fallback: copy URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  // Get category name from category_id (you might want to fetch this from an API too)
  const getCategoryName = (categoryId) => {
    const categories = {
      1: "Technology",
      2: "Business",
      3: "Health",
      4: "Science",
      5: "Entertainment",
      6: "Politics",
      7: "Sports",
      8: "Travel",
      9: "Food",
      10: "Lifestyle",
    };
    return categories[categoryId] || "General";
  };

  // Styled components
  const StyledContent = styled("div")(({ theme }) => ({
    "& p": {
      marginBottom: theme.spacing(2),
      lineHeight: 1.7,
    },
    "& h2": {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
      fontWeight: 600,
      scrollMarginTop: "80px",
    },
    "& h3": {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1.5),
      fontWeight: 500,
      scrollMarginTop: "80px",
    },
    "& ul, & ol": {
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(4),
    },
    "& li": {
      marginBottom: theme.spacing(1),
    },
    "& img": {
      maxWidth: "100%",
      height: "auto",
      borderRadius: theme.shape.borderRadius,
      marginBottom: theme.spacing(2),
    },
    "& blockquote": {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      paddingLeft: theme.spacing(2),
      fontStyle: "italic",
      margin: theme.spacing(2, 0),
    },
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
    "& code": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.05)",
      padding: theme.spacing(0.5, 1),
      borderRadius: theme.shape.borderRadius,
      fontFamily: "monospace",
    },
  }));

  // Loading state
  if (loading) {
    return (
      <Box sx={{ bgcolor: "background.default", py: 4 }}>
        <Container maxWidth="lg">
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ bgcolor: "background.default", py: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 4 }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  // No post found
  if (!post) {
    return (
      <Box sx={{ bgcolor: "background.default", py: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="info" sx={{ mt: 4 }}>
            Post not found.
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "background.default", py: 4 }}>
      <Container maxWidth="lg">
        {/* Top Banner */}
        {post.show_top_banner && post.top_banner_name && (
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <img
              src={publicUploadUrl(post.top_banner_name)}
              alt="Top Banner"
              style={{
                maxWidth: "100%",
                height: "auto",
                maxHeight: "200px",
                objectFit: "contain",
              }}
            />
          </Box>
        )}

        {/* Breadcrumbs */}
        <Box sx={{ mb: 4, display: "flex", alignItems: "center" }}>
          <Typography
            variant="body2"
            component="a"
            href="/"
            sx={{
              color: "text.secondary",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
                color: "primary.main",
              },
            }}
          >
            Home
          </Typography>
          <Typography variant="body2" sx={{ mx: 1, color: "text.secondary" }}>
            /
          </Typography>
          <Typography
            variant="body2"
            component="a"
            href="/articles"
            sx={{
              color: "text.secondary",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
                color: "primary.main",
              },
            }}
          >
            Articles
          </Typography>
          <Typography variant="body2" sx={{ mx: 1, color: "text.secondary" }}>
            /
          </Typography>
          <Typography variant="body2" sx={{ color: "text.primary" }}>
            {post.title}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Main content */}
          <Grid item xs={12} md={8}>
            <Card elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
              {/* Featured image */}
              {post.featured_image_name && (
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    component="img"
                    height={400}
                    image={publicUploadUrl(post.featured_image_name)}
                    alt={post.title}
                    sx={{
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: "100%",
                      bgcolor: "rgba(0, 0, 0, 0.5)",
                      color: "white",
                      p: 3,
                      backgroundImage:
                        "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)",
                    }}
                  >
                    <Chip
                      label={getCategoryName(post.category_id)}
                      size="small"
                      sx={{
                        mr: 1,
                        mb: 1,
                        bgcolor: "primary.main",
                        color: "white",
                        fontWeight: 500,
                      }}
                    />
                    <Typography
                      variant={isMobile ? "h5" : "h4"}
                      component="h1"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                      }}
                    >
                      {post.title}
                    </Typography>
                  </Box>
                </Box>
              )}

              <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                {/* Article meta */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 3,
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  {post.show_author !== 0 && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{ width: 40, height: 40, mr: 1.5 }}
                      src="https://randomuser.me/api/portraits/men/32.jpg"
                    >
                      <Person />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Admin
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Author
                      </Typography>
                    </Box>
                  </Box>
                  )}
                  <Divider orientation="vertical" flexItem />

                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <AccessTime
                      sx={{ fontSize: 16, mr: 0.5, color: "text.secondary" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(post.createdAt)}
                    </Typography>
                  </Box>

                  <Divider orientation="vertical" flexItem />

                  <Typography variant="body2" color="text.secondary">
                    {post.status}
                  </Typography>
                </Box>
                

                {/* Article content */}
                <StyledContent
                  dangerouslySetInnerHTML={{ __html: post.description }}
                />

                <Divider sx={{ my: 4 }} />

                {/* Action buttons */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={liked ? <ThumbUp /> : <ThumbUpOutlined />}
                      onClick={handleLikeClick}
                      sx={{
                        borderRadius: 20,
                        textTransform: "none",
                        color: liked ? "primary.main" : "text.secondary",
                        borderColor: liked ? "primary.main" : "text.secondary",
                      }}
                    >
                      {likeCount}
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<Comment />}
                      sx={{
                        borderRadius: 20,
                        textTransform: "none",
                        color: "text.secondary",
                        borderColor: "text.secondary",
                      }}
                    >
                      Comment
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<Share />}
                      onClick={handleShareClick}
                      sx={{
                        borderRadius: 20,
                        textTransform: "none",
                        color: "text.secondary",
                        borderColor: "text.secondary",
                      }}
                    >
                      Share
                    </Button>
                  </Box>

                  <Button
                    variant="outlined"
                    startIcon={bookmarked ? <Bookmark /> : <BookmarkBorder />}
                    onClick={handleBookmarkClick}
                    sx={{
                      borderRadius: 20,
                      textTransform: "none",
                      color: bookmarked ? "primary.main" : "text.secondary",
                      borderColor: bookmarked
                        ? "primary.main"
                        : "text.secondary",
                    }}
                  >
                    {bookmarked ? "Saved" : "Save"}
                  </Button>
                </Box>

                {/* Comments Section */}
                <Comments postId={post.id} />
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* Author info */}
            {post.show_author !== 0 && (
            <Card elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  About the Author
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar
                    sx={{ width: 60, height: 60, mr: 2 }}
                    src="https://randomuser.me/api/portraits/men/32.jpg"
                  >
                    <Person />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Admin
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Content Creator
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Creating engaging content and sharing insights on various
                  topics.
                </Typography>
              </CardContent>
            </Card>
            )}
            {/* Article stats */}
            <Card elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Article Stats
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Published:
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(post.createdAt)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Category:
                  </Typography>
                  <Typography variant="body2">
                    {getCategoryName(post.category_id)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip
                    label={post.status}
                    size="small"
                    color={post.status === "published" ? "success" : "default"}
                  />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    Last Updated:
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(post.updatedAt)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Share options */}
            <Card elevation={1} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Share this article
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleShareClick}
                    sx={{ textTransform: "none" }}
                  >
                    Copy Link
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                      post.title
                    )}&url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    sx={{ textTransform: "none" }}
                  >
                    Twitter
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                      window.location.href
                    )}`}
                    target="_blank"
                    sx={{ textTransform: "none" }}
                  >
                    Facebook
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                      window.location.href
                    )}`}
                    target="_blank"
                    sx={{ textTransform: "none" }}
                  >
                    LinkedIn
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Bottom Banner */}
        {post.show_bottom_banner && post.bottom_banner_name && (
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <img
              src={publicUploadUrl(post.bottom_banner_name)}
              alt="Bottom Banner"
              style={{
                maxWidth: "100%",
                height: "auto",
                maxHeight: "200px",
                objectFit: "contain",
              }}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Post;
